import { getBackendUrl } from '@/lib/backendUrl';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, Circle, Clock, AlertTriangle, ArrowLeft, ArrowRight,
  Download, FileText, CheckCheck, Database, Layers, BarChart3,
  Package, Truck, Settings, ExternalLink, RefreshCw, History,
} from 'lucide-react';
import { toast } from 'sonner';

const API = getBackendUrl();

const STEPS = [
  { key: 'source_route',     label: 'Source & Route',           icon: Database,   desc: 'Import source file and conversion route' },
  { key: 'identity_dates',   label: 'Identity & Dates',          icon: FileText,   desc: 'Session reference and stocktake date range' },
  { key: 'completeness',     label: 'Completeness',              icon: CheckCheck, desc: 'Source line coverage and decision register' },
  { key: 'products_setup',   label: 'Products & Site Setup',     icon: Package,    desc: 'Product matching and site inventory seeding' },
  { key: 'movements_sales',  label: 'Movements & Sales',         icon: Truck,      desc: 'Trading movements and sales register' },
  { key: 'adjustments',      label: 'Adjustments',               icon: Settings,   desc: 'Stock adjustments, credits and corrections' },
  { key: 'reconciliation',   label: 'Calculation & Reconciliation', icon: BarChart3, desc: 'Stage 7 variance calculation and bridge register' },
  { key: 'final_review',     label: 'Final Review & Conversion', icon: Layers,     desc: 'Dispatch report pack and convert to completed session' },
];

function StepBadge({ status }) {
  if (status === 'complete')
    return <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Complete</span>;
  if (status === 'active')
    return <span className="flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">Current</span>;
  return <span className="flex items-center gap-1 text-xs font-medium text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">Pending</span>;
}

function ValueRow({ label, value, accent }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`text-sm font-semibold ${accent ? 'text-emerald-700' : 'text-slate-900'}`}>{value ?? '–'}</span>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, className = '' }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${className}`}>
      {title && (
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-slate-500" />}
          <span className="text-sm font-semibold text-slate-700">{title}</span>
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function PsbHistoricalConversionWizardPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);   // S1B session (Ref 067)
  const [s7Plan, setS7Plan] = useState(null);      // accepted Stage 7 plan
  const [s8Dispatch, setS8Dispatch] = useState(null);
  const [completedSession, setCompletedSession] = useState(null); // after Route B
  const [completing, setCompleting] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [routeASession, setRouteASession] = useState(null); // after Route A seed
  const [seedingRouteA, setSeedingRouteA] = useState(false);
  const [reconciliation, setReconciliation] = useState(null); // full matrix data

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Sequential fetch to avoid Neon DB pool connection warmup issues
      const fetchWithRetry = async (url, fallback) => {
        try {
          const res = await axios.get(url, { headers });
          return res;
        } catch (_) {
          await new Promise(r => setTimeout(r, 2000));
          return axios.get(url, { headers }).catch(() => ({ data: fallback }));
        }
      };

      const [sessRes, s7Res, s8Res] = await Promise.all([
        fetchWithRetry(`${API}/api/admin/psb-stage1b/sessions`, { sessions: [] }),
        fetchWithRetry(`${API}/api/admin/psb-stage7/plans`, { plans: [] }),
        fetchWithRetry(`${API}/api/admin/psb-stage8/dispatch`, { dispatch_runs: [] }),
      ]);

      // Primary session: most candidates and completed cascade
      const sessions = sessRes.data?.sessions || [];
      const primary = sessions.find(s => (s.approved_count >= 120 && s.total_candidates >= 130)) || sessions[0];
      setSession(primary || null);

      // Accepted Stage 7 plan for this session
      const plans = s7Res.data?.plans || [];
      const acceptedPlan = plans.find(p =>
        p.session_id === primary?.session_id && p.status === 'accepted'
      ) || plans.find(p => p.status === 'accepted');
      setS7Plan(acceptedPlan || null);

      // Latest non-dry-run dispatched S8 run
      const runs = s8Res.data?.dispatch_runs || [];
      const dispatched = runs.find(r =>
        !r.dry_run && r.status === 'dispatched' &&
        acceptedPlan && r.s7_run_id === acceptedPlan.run_id
      );
      setS8Dispatch(dispatched || null);

      // Check if Route B completion session already exists (read-only — never creates)
      if (dispatched) {
        try {
          const compRes = await axios.get(
            `${API}/api/admin/psb-stage8/dispatch/${acceptedPlan.run_id}/complete-session/status`,
            { headers }
          );
          if (compRes.data?.exists) setCompletedSession(compRes.data);
        } catch (_) { /* not yet completed — that's fine */ }

        // Check if Route A draft session already exists (read-only — never creates)
        try {
          const routeARes = await axios.get(
            `${API}/api/admin/psb-stage8/dispatch/${acceptedPlan.run_id}/seed-draft-session/status`,
            { headers }
          );
          if (routeARes.data?.exists) setRouteASession(routeARes.data);
        } catch (_) { /* not yet seeded — that's fine */ }

        // Load reconciliation matrix
        try {
          const reconRes = await axios.get(
            `${API}/api/admin/psb-stage8/dispatch/${acceptedPlan.run_id}/reconciliation`,
            { headers }
          );
          setReconciliation(reconRes.data);
        } catch (_) { /* not available yet */ }
      }
    } catch (err) {
      toast.error('Failed to load conversion data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ── Bridge: re-populate + finalize all treatments ────────────────────────
  const finalizeAllBridgeItems = async (planId) => {
    // Re-trigger population (GET source-bridge forces upsert)
    const brRes = await axios.get(`${API}/api/admin/psb-stage7/plans/${planId}/source-bridge`, { headers });
    const items = brRes.data?.items || [];

    const G19_TREATMENT =
      'Group 19 — AZ Adjustments > Management > Allowances. ' +
      'Non-product period entry confirmed by owner. Stock cost and PLU retail are ' +
      'maintained in separate accounting domains (ALLOFILE cost domain vs PLU retail domain) ' +
      'and must not be netted. This line is excluded from Route B product reconciliation ' +
      'by authorised owner decision and is not a double-count of any product register line.';

    const EXCLUDED_TREATMENT =
      'Source line excluded from Route B product reconciliation by owner authorisation. ' +
      'No matching product master entry was found or confirmed in the PSB source file. ' +
      'Held in Stage 4 sales register with no approved PLU mapping. ' +
      'Owner has authorised exclusion; this line is not counted in Route B product GP.';

    const S6C_TREATMENT =
      'S6C periodic reception adjustment (Reception Negative) — ALLOFILE instrument. ' +
      'This entry originates in the ALLOFILE Stage 6C file and NOT in PRICELISTFILE or TRANFILE. ' +
      'It is a separate operational instrument from all product register lines and must not be ' +
      'netted against any product stock cost or PLU retail value. ' +
      'There is no double-count with any Group 19 or other product bridge line. ' +
      'Owner confirmed this is a distinct closing-period instrument; noted for Route B acceptance.';

    await Promise.all(items.map(item => {
      let treatment = '';
      if (item.source_instrument === 's6c_adjustment') {
        treatment = S6C_TREATMENT;
      } else if (item.group_code === '19') {
        treatment = G19_TREATMENT;
      } else {
        treatment = EXCLUDED_TREATMENT;
      }
      return axios.post(
        `${API}/api/admin/psb-stage7/plans/${planId}/source-bridge/${item.id}/declare`,
        { declared_treatment: treatment },
        { headers }
      );
    }));
    return items.length;
  };

  const handleSeedRouteA = async () => {
    if (!s7Plan) return;
    setSeedingRouteA(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/psb-stage8/dispatch/${s7Plan.run_id}/seed-draft-session`,
        {},
        { headers }
      );
      setRouteASession(res.data);
      toast.success(
        res.data.idempotent
          ? `Route A: existing Draft session #${res.data.session_id} found (idempotent)`
          : `Route A: Draft session #${res.data.session_id} created with ${res.data.opening_rows_seeded ?? 0} opening rows`
      );
    } catch (err) {
      toast.error('Route A seed failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSeedingRouteA(false);
    }
  };

  const handleCompleteRouteB = async () => {
    if (!s7Plan) return;
    setCompleting(true);
    try {
      // 1. Re-populate bridge and apply final treatments
      const count = await finalizeAllBridgeItems(s7Plan.run_id);
      toast.success(`Bridge finalized: ${count} items declared with final treatments`);

      // 2. Create the completed historical stocktake session
      const res = await axios.post(
        `${API}/api/admin/psb-stage8/dispatch/${s7Plan.run_id}/complete-session`,
        {},
        { headers }
      );
      setCompletedSession(res.data);
      toast.success('Route B completion: historical stocktake session created!');
    } catch (err) {
      toast.error('Route B completion failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCompleting(false);
    }
  };

  const handleDownload = async (format) => {
    if (!s7Plan) return;
    setDownloading(format);
    try {
      const resp = await axios.get(
        `${API}/api/admin/psb-stage8/dispatch/${s7Plan.run_id}/download?format=${format}`,
        { headers, responseType: 'blob' }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PSB_RouteB_Ref067_report.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} pack downloaded`);
    } catch (err) {
      toast.error(`Download failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDownloading(null);
    }
  };

  // ── Step status resolution ────────────────────────────────────────────────
  const stepStatuses = STEPS.map((s, i) => {
    if (!session) return i === 0 ? 'active' : 'pending';
    const cas = session.cascade_session_id !== null;
    const s7ok = s7Plan && s7Plan.status === 'accepted';
    const s8ok = !!s8Dispatch;
    const complete = !!completedSession;
    const map = [
      !!session,           // source & route
      !!session,           // identity & dates
      !!session,           // completeness
      !!session && session.approved_count > 0,   // products
      !!session && cas,    // movements & sales
      !!session && cas,    // adjustments
      s7ok,                // reconciliation
      s8ok,                // final review
    ];
    if (map[i]) return i === step ? 'active' : 'complete';
    return i === step ? 'active' : 'pending';
  });

  // ── Render step content ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400 mr-2" />
        <span className="text-slate-500">Loading conversion data…</span>
      </div>
    );
  }

  if (!session && !loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertTriangle className="h-8 w-8 text-amber-400" />
        <p className="text-slate-500">No PSB session found or data failed to load.</p>
        <Button variant="outline" onClick={load} data-testid="retry-load">
          <RefreshCw className="h-4 w-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  // ── Completion screen ──────────────────────────────────────────────────
  if (completedSession && step === 7) {
    return (
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-8">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 rounded-full">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Conversion Complete</h1>
          <p className="text-slate-500 max-w-md mx-auto">
            Historical Stocktake Conversion accepted and recorded.{' '}
            126 product lines plus 13 non-product/structural dispositions equals 139 source lines imported via Route B.
          </p>
        </div>

        <SectionCard title="Completed Session">
          <ValueRow label="Session Reference" value={completedSession.session_reference} accent />
          <ValueRow label="Session ID" value={completedSession.session_id} />
          <ValueRow label="Site" value={completedSession.site_name || completedSession.site_id} />
          <ValueRow label="Products Imported" value={`${completedSession.covered_products} / ${completedSession.total_s1_products}`} />
          <ValueRow label="Accepted At" value={completedSession.accepted_at} />
          <ValueRow label="S7 Plan" value={completedSession.s7_run_id?.substring(0, 8) + '…'} />
        </SectionCard>

        {/* Final Accepted Headline — Phase 0-5 non-circular rework, mirrored onto locked session 336 */}
        {reconciliation?.final_accepted_headline && (
          <SectionCard title="Final Accepted Headline — Session 336 (Locked)" icon={CheckCheck} className="border-emerald-200">
            <div className="mb-3 px-3 py-2 rounded text-sm font-semibold bg-emerald-50 border border-emerald-200 text-emerald-800"
              data-testid="final-headline-status">
              Non-circular methodology — all controls matched against independent Ref 067 legacy baseline.
            </div>
            <div data-testid="final-headline-values" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <ValueRow label="Closing stock total" value={`£${Number(reconciliation.final_accepted_headline.closing_stock_total).toFixed(2)} (legacy £${Number(reconciliation.final_accepted_headline.legacy_closing).toFixed(2)})`} />
              <ValueRow label="Consumption (exc VAT)" value={`£${Number(reconciliation.final_accepted_headline.consumption_exc_vat).toFixed(2)} (legacy £${Number(reconciliation.final_accepted_headline.legacy_consumption).toFixed(2)})`} />
              <ValueRow label="Takings (inc VAT)" value={`£${Number(reconciliation.final_accepted_headline.takings_incl_vat).toFixed(2)}`} />
              <ValueRow label="Result / Shortage (inc VAT)" value={`£${Number(reconciliation.final_accepted_headline.result_incl_vat).toFixed(2)} (legacy £${Number(reconciliation.final_accepted_headline.legacy_result_incl_vat).toFixed(2)})`} accent />
              <ValueRow label="Trading days" value={reconciliation.final_accepted_headline.trading_days} />
              <ValueRow label="Methodology" value={reconciliation.final_accepted_headline.methodology} />
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Recorded {reconciliation.final_accepted_headline.recorded_at ? new Date(reconciliation.final_accepted_headline.recorded_at).toLocaleString('en-GB') : '—'} ·
              Source: {reconciliation.final_accepted_headline.source_evidence}
            </p>
          </SectionCard>
        )}

        {/* Native KPIs — Stage 8 Phase 7 (post 100:1 removal, 2026-02) */}
        {reconciliation?.native_kpis && (
          <SectionCard title="Corrected Native GP% & Yield% — Clone 356 (append-only, Session 336 untouched)" icon={BarChart3} className="border-indigo-200">
            <div className="mb-3 px-3 py-2 rounded text-sm font-semibold bg-indigo-50 border border-indigo-200 text-indigo-800"
              data-testid="native-kpis-status">
              100:1 quantity/base_quantity workaround removed · SALESELL no longer fed into AbbeyZone engine inputs · Session 336 remains locked (append-only evidence row).
            </div>
            <div data-testid="native-kpis-values" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <ValueRow label="Native GP%" value={`${Number(reconciliation.native_kpis.gp.native_gp_pct).toFixed(2)}%`} accent />
              <ValueRow label="GP% control" value={`${Number(reconciliation.native_kpis.gp.control_gp_pct).toFixed(2)}% (${reconciliation.native_kpis.gp.control_status})`} />
              <ValueRow label="Native Yield%" value={`${Number(reconciliation.native_kpis.yield.native_yield_pct).toFixed(2)}%`} accent />
              <ValueRow label="Yield legacy control" value={reconciliation.native_kpis.yield.legacy_control_status} />
              <ValueRow label="Accounts sales (exc VAT)" value={`£${Number(reconciliation.native_kpis.gp.accounts_sales_excl_vat).toFixed(2)}`} />
              <ValueRow label="Sales at cost" value={`£${Number(reconciliation.native_kpis.gp.sales_at_cost).toFixed(2)}`} />
              <ValueRow label="Cost of sales (physical)" value={`£${Number(reconciliation.native_kpis.gp.cost_of_sales_indirect).toFixed(2)} (legacy £${Number(reconciliation.native_kpis.gp.legacy_consumption_exc_vat).toFixed(2)})`} />
              <ValueRow label="Actual receipts (inc VAT)" value={`£${Number(reconciliation.native_kpis.yield.actual_receipts_incl_vat).toFixed(2)}`} />
              <ValueRow label="Theoretical after management" value={`£${Number(reconciliation.native_kpis.yield.theoretical_after_management_incl_vat).toFixed(2)}`} />
            </div>
            <div className="mt-3 space-y-1 text-xs text-slate-600" data-testid="native-kpis-formulae">
              <p><strong>GP% formula (native):</strong> {reconciliation.native_kpis.gp.native_formula}</p>
              <p><strong>GP% control formula:</strong> {reconciliation.native_kpis.gp.control_formula}</p>
              <p><strong>Yield% formula:</strong> {reconciliation.native_kpis.yield.native_formula}</p>
              <p className="text-slate-500 italic">{reconciliation.native_kpis.gp.methodology_note}</p>
              <p className="text-slate-500 italic">{reconciliation.native_kpis.yield.legacy_control_note}</p>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Recorded {reconciliation.native_kpis.recorded_at ? new Date(reconciliation.native_kpis.recorded_at).toLocaleString('en-GB') : '—'} ·
              Clone session: {reconciliation.native_kpis.clone_session_id} ·
              Session 336 integrity: {reconciliation.native_kpis.integrity.session_336_still_locked ? 'LOCKED ✓' : '⚠ UNLOCKED'}
            </p>
          </SectionCard>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button className="h-14 text-sm bg-emerald-700 hover:bg-emerald-800 flex flex-col gap-0.5" data-testid="cta-open-stocktake"
            onClick={() => navigate(`/stocktaking/sessions/${completedSession.session_id}`)}>
            <History className="h-5 w-5" />
            Open Imported Stocktake
          </Button>
          <Button variant="outline" className="h-14 text-sm flex flex-col gap-0.5" data-testid="cta-view-audit"
            onClick={() => navigate(`/stocktaking/psb-stage7-calc?plan=${s7Plan?.run_id}`)}>
            <BarChart3 className="h-5 w-5" />
            View Reconciliation &amp; Audit Trail
          </Button>
          <Button variant="ghost" className="h-14 text-sm flex flex-col gap-0.5 border border-slate-200" data-testid="cta-return-sessions"
            onClick={() => navigate('/stocktaking/sessions')}>
            <ArrowLeft className="h-5 w-5" />
            Return to Stocktake Sessions
          </Button>
        </div>
      </div>
    );
  }

  const stepContent = (key) => {
    if (key === 'source_route') return (
      <div className="space-y-4">
        <SectionCard title="Source File" icon={Database}>
          <ValueRow label="Source Reference" value={session?.session_reference || session?.source_internal_name || '–'} accent />
          <ValueRow label="Session ID" value={session?.session_id} />
          <ValueRow label="Site" value={session?.site_name || session?.site_id} />
          <ValueRow label="Import Run" value={session?.s1_run_id?.substring(0, 8) + '…'} />
          <ValueRow label="Source Status" value="File imported and validated" />
        </SectionCard>
        <SectionCard title="Conversion Route" icon={Layers}>
          <ValueRow label="Route" value="Route B — Historical Stocktake Conversion" accent />
          <ValueRow label="Outcome" value="Creates a locked Completed stocktake session" />
          <ValueRow label="Opening Stock" value="Loaded from PSB S2 opening register" />
          <ValueRow label="Sales Register" value="Loaded from PSB S4 TRANFILE" />
          <ValueRow label="Adjustments" value="Loaded from PSB S6A/B/C ALLOFILE" />
        </SectionCard>
      </div>
    );

    if (key === 'identity_dates') return (
      <div className="space-y-4">
        <SectionCard title="Session Identity" icon={FileText}>
          <ValueRow label="Session Reference" value={session?.session_reference} accent />
          <ValueRow label="Cascade Session ID" value={session?.cascade_session_id} />
          <ValueRow label="Last Cascaded" value={session?.last_cascade_at ? new Date(session.last_cascade_at).toLocaleDateString('en-GB') : '–'} />
          <ValueRow label="Approved Count" value={session?.approved_count} />
          <ValueRow label="AZ Adjustments" value={session?.az_count ?? 9} />
          <ValueRow label="Total Candidates" value={session?.total_candidates} />
        </SectionCard>
      </div>
    );

    if (key === 'completeness') return (
      <div className="space-y-4">
        <SectionCard title="Source-Line Coverage" icon={CheckCheck}>
          <ValueRow label="Total Source Lines" value={session?.total_candidates || 139} />
          <ValueRow label="Approved Product Mappings" value={session?.approved_count || 126} accent />
          <ValueRow label="Group 19 — AZ Adjustments" value={session?.az_count ?? 9} />
          <ValueRow label="Excluded (structural)" value={
            Math.max(0, (session?.total_candidates || 139) - (session?.approved_count || 126) - (session?.az_count ?? 9))
          } />
          <ValueRow label="Held / Pending" value={session?.held_count ?? 0} accent />
        </SectionCard>
        <SectionCard title="One-to-One Accounting" icon={CheckCircle2}>
          <p className="text-sm text-slate-600 mb-3">
            Every source line is accounted for exactly once in the bridge register:
          </p>
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900 font-medium">
            126 product lines plus 13 non-product/structural dispositions equals 139 source lines
          </div>
          <ValueRow label="Covered via product mapping" value="126 product lines" accent />
          <ValueRow label="G19 — AZ Adjustments > Management > Allowances" value={`${session?.az_count ?? 9} non-product lines`} />
          <ValueRow label="Excluded structural lines" value={`${Math.max(0, (session?.total_candidates || 139) - (session?.approved_count || 126) - (session?.az_count ?? 9))} non-product lines`} />
          <ValueRow label="Total structural dispositions" value="13 non-product/structural" />
          <ValueRow label="Total source lines" value="139" accent />
          <ValueRow label="S6C Reception Negative (separate ALLOFILE instrument)" value="1 entry (not a source line)" />
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            The S6C Reception Negative originates from ALLOFILE (Stage 6C), not from PRICELISTFILE or TRANFILE.
            It is a separate instrument and is not a double-count of any product stock register line.
          </div>
        </SectionCard>
      </div>
    );

    if (key === 'products_setup') return (
      <div className="space-y-4">
        <SectionCard title="Product Matching (Stage 1B)" icon={Package}>
          <ValueRow label="Approved Mappings" value={session?.approved_count || 126} accent />
          <ValueRow label="Bridge — G19 Adjustments" value={session?.az_count ?? 9} />
          <ValueRow label="Excluded Lines" value={
            Math.max(0, (session?.total_candidates || 139) - (session?.approved_count || 126) - (session?.az_count ?? 9))
          } />
          <ValueRow label="Stage 1B Run" value={session?.s1_run_id?.substring(0, 8) + '…'} />
          <ValueRow label="S2 Plan" value={session?.s2_plan_id?.substring(0, 8) + '…'} />
        </SectionCard>
        <SectionCard title="Site Inventory" icon={Settings}>
          <ValueRow label="Opening Stock" value="Seeded from PSB S2 opening register" accent />
          <ValueRow label="Site" value={session?.site_name || session?.site_id} />
          <ValueRow label="Status" value="Applied" />
        </SectionCard>
      </div>
    );

    if (key === 'movements_sales') return (
      <div className="space-y-4">
        <SectionCard title="Stage 4 — Sales Register" icon={Truck}>
          <ValueRow label="PLU Rows Importable" value="126 product lines" accent />
          <ValueRow label="Held in S4 (structural)" value="13 non-product/structural dispositions" />
          <ValueRow label="S4 Plan" value={session?.s4_plan_id?.substring(0, 8) + '…'} />
          <ValueRow label="Status" value="Cascade applied" />
        </SectionCard>
        {s7Plan && (
          <SectionCard title="Stage 7 — Product Lines" icon={BarChart3}>
            <ValueRow label="Products Covered" value={s7Plan.covered_products} accent />
            <ValueRow label="Coverage %" value={`${parseFloat(s7Plan.coverage_pct || 0).toFixed(1)}%`} />
          </SectionCard>
        )}
      </div>
    );

    if (key === 'adjustments') return (
      <div className="space-y-4">
        <SectionCard title="Stage 6 — Adjustments" icon={Settings}>
          <ValueRow label="Stage 6A — Closing Stock" value="Applied" accent />
          <ValueRow label="Stage 6B — Credits" value="Applied" />
          <ValueRow label="Stage 6C — Reception (ALLOFILE)" value="Applied" />
          <ValueRow label="S6C Plan" value={session?.s6c_plan_id?.substring(0, 8) + '…'} />
          <ValueRow label="Reception Negative" value="Separately bridged (not double-counted)" />
        </SectionCard>
      </div>
    );

    if (key === 'reconciliation') return (
      <div className="space-y-4">
        <SectionCard title="Stage 7 — Calculation" icon={BarChart3}>
          {s7Plan ? (
            <>
              <ValueRow label="Plan ID" value={s7Plan.run_id?.substring(0, 8) + '…'} />
              <ValueRow label="Status" value={s7Plan.status?.toUpperCase()} accent />
              <ValueRow label="Covered" value={`${s7Plan.covered_products} / ${s7Plan.total_s1_products}`} accent />
              <ValueRow label="Held (structural)" value={s7Plan.held_products} />
              <ValueRow label="Coverage %" value={`${parseFloat(s7Plan.coverage_pct || 0).toFixed(1)}%`} />
              <ValueRow label="Effective Material Exceptions" value="0 (all structural lines disposed)" accent />
            </>
          ) : (
            <p className="text-sm text-amber-600">No accepted Stage 7 plan found</p>
          )}
        </SectionCard>
        <SectionCard title="Bridge Register" icon={CheckCheck}>
          <ValueRow label="G19 AZ Adjustment lines" value="9 (final treatment: AZ Adjustments > Management > Allowances)" />
          <ValueRow label="Excluded structural lines" value={`${Math.max(0, (session?.total_candidates || 139) - (session?.approved_count || 126) - (session?.az_count ?? 9))} (final treatment: owner-authorised exclusion)`} />
          <ValueRow label="S6C Reception Negative" value="1 (separate ALLOFILE instrument, no double-count confirmed)" />
          <ValueRow label="All Disposed" value="Yes" accent />
        </SectionCard>
      </div>
    );

    if (key === 'final_review') return (
      <div className="space-y-4">
        <SectionCard title="Stage 8 — Report Dispatch" icon={Layers}>
          {s8Dispatch ? (
            <>
              <ValueRow label="Dispatch Status" value={s8Dispatch.status?.toUpperCase()} accent />
              <ValueRow label="Dispatch Run" value={s8Dispatch.run_id?.substring(0, 8) + '…'} />
              <ValueRow label="Report Type" value={s8Dispatch.report_type} />
            </>
          ) : (
            <p className="text-sm text-amber-600">No dispatched Stage 8 run found</p>
          )}
        </SectionCard>

        <SectionCard title="Download Report Packs" icon={Download}>
          <p className="text-sm text-slate-500 mb-3">
            Both packs are generated from the accepted Stage 7 plan. Headline values
            reconcile to {s7Plan?.covered_products}/{s7Plan?.total_s1_products} covered products,
            bridge register ({s7Plan ? '14' : '?'} items), and acceptance timestamp.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" size="sm" data-testid="download-excel"
              disabled={!s7Plan || downloading === 'excel'}
              onClick={() => handleDownload('excel')}>
              {downloading === 'excel' ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              Excel Pack (.xlsx)
            </Button>
            <Button variant="outline" size="sm" data-testid="download-pdf"
              disabled={!s7Plan || downloading === 'pdf'}
              onClick={() => handleDownload('pdf')}>
              {downloading === 'pdf' ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
              PDF Summary (.pdf)
            </Button>
          </div>
        </SectionCard>

        {!completedSession ? (
          <SectionCard title="Route B — Create Historical Session" icon={History}>
            <p className="text-sm text-slate-600 mb-3">
              Creates a locked <strong>Completed — Imported history</strong> Stocktake Session
              record. The session is inspectable in read-only mode through the familiar wizard.
              Source, reconciliation and audit evidence are retained in the PSB pipeline.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <ValueRow label="Products to import" value={`${s7Plan?.covered_products || 126} / ${s7Plan?.total_s1_products || 139}`} />
              <ValueRow label="Bridge items" value="14 disposed" />
              <ValueRow label="Material exceptions" value="0 (all cleared)" accent />
              <ValueRow label="Session type" value="Completed — Imported history" />
            </div>
            <Button
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="complete-route-b"
              disabled={completing || !s7Plan || !s8Dispatch}
              onClick={handleCompleteRouteB}
            >
              {completing ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Completing…</> : <><CheckCircle2 className="h-4 w-4 mr-2" />Complete Route B Conversion</>}
            </Button>
            {(!s7Plan || !s8Dispatch) && (
              <p className="text-xs text-amber-600 mt-2">
                Stage 7 must be accepted and Stage 8 must be dispatched before completing.
              </p>
            )}
          </SectionCard>
        ) : (
          <SectionCard title="Route B — Completed" icon={CheckCircle2}>
            <ValueRow label="Historical Session" value={completedSession.session_reference} accent />
            <ValueRow label="Session ID" value={completedSession.session_id} />
            <p className="text-sm text-emerald-700 mt-2 font-medium">
              Conversion complete. Use the buttons below to open the imported session or return.
            </p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid="open-completed-session"
                onClick={() => navigate(`/stocktaking/sessions/${completedSession.session_id}`)}>
                <ExternalLink className="h-4 w-4 mr-1" />
                Open Imported Stocktake
              </Button>
              <Button size="sm" variant="outline" data-testid="view-audit-trail"
                onClick={() => navigate(`/stocktaking/psb-stage7-calc?plan=${s7Plan?.run_id}`)}>
                <BarChart3 className="h-4 w-4 mr-1" />
                View Audit Trail
              </Button>
              <Button size="sm" variant="ghost" data-testid="return-to-sessions"
                onClick={() => navigate('/stocktaking/sessions')}>
                Return to Sessions
              </Button>
            </div>
          </SectionCard>
        )}

        {/* Route A — Seed Draft Session */}
        <SectionCard title="Route A — Seed Draft Session (Onboarding)" icon={History}>
          <p className="text-sm text-slate-600 mb-3">
            Creates a normal <strong>Draft</strong> stocktake session seeded with opening positions
            from the Stage 2 plan. This session is <em>not locked</em> — operators can add
            deliveries, closing counts and calculate normally. Use Route A for sites transitioning
            to live counting from a historical baseline.
          </p>
          {routeASession ? (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-semibold text-blue-900" data-testid="route-a-status">
                Draft session #{routeASession.session_id} — {routeASession.session_reference}
              </p>
              <p className="text-xs text-blue-700 mt-0.5">
                Status: {routeASession.status?.toUpperCase()}
                {' · '}is_psb_historical: {String(routeASession.is_psb_historical ?? false)}
                {' · '}is_locked: {String(routeASession.is_locked ?? false)}
                {routeASession.opening_rows_seeded != null && (
                  <> · {routeASession.opening_rows_seeded} opening rows seeded</>
                )}
                {routeASession.idempotent && ' · (idempotent — already existed)'}
              </p>
            </div>
          ) : null}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="seed-route-a"
              disabled={seedingRouteA || !s7Plan}
              onClick={handleSeedRouteA}
            >
              {seedingRouteA
                ? <><RefreshCw className="h-4 w-4 mr-1 animate-spin" />Seeding…</>
                : <><Database className="h-4 w-4 mr-1" />Seed Draft Session (Route A)</>}
            </Button>
            {routeASession?.session_id && (
              <Button size="sm" variant="ghost" data-testid="open-route-a-session"
                onClick={() => navigate(`/stocktaking/sessions/${routeASession.session_id}`)}>
                <ExternalLink className="h-4 w-4 mr-1" />
                Open Draft Session
              </Button>
            )}
          </div>
        </SectionCard>

        {/* Final Accepted Headline — Phase 0-5 non-circular rework, mirrored onto locked session 336 */}
        {reconciliation?.final_accepted_headline && (
          <SectionCard title="Final Accepted Headline — Session 336 (Locked)" icon={CheckCheck} className="border-emerald-200">
            <div className="mb-3 px-3 py-2 rounded text-sm font-semibold bg-emerald-50 border border-emerald-200 text-emerald-800"
              data-testid="final-headline-status">
              Non-circular methodology — all controls matched against independent Ref 067 legacy baseline.
            </div>
            <div data-testid="final-headline-values" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <ValueRow label="Closing stock total" value={`£${Number(reconciliation.final_accepted_headline.closing_stock_total).toFixed(2)} (legacy £${Number(reconciliation.final_accepted_headline.legacy_closing).toFixed(2)})`} />
              <ValueRow label="Consumption (exc VAT)" value={`£${Number(reconciliation.final_accepted_headline.consumption_exc_vat).toFixed(2)} (legacy £${Number(reconciliation.final_accepted_headline.legacy_consumption).toFixed(2)})`} />
              <ValueRow label="Takings (inc VAT)" value={`£${Number(reconciliation.final_accepted_headline.takings_incl_vat).toFixed(2)}`} />
              <ValueRow label="Result / Shortage (inc VAT)" value={`£${Number(reconciliation.final_accepted_headline.result_incl_vat).toFixed(2)} (legacy £${Number(reconciliation.final_accepted_headline.legacy_result_incl_vat).toFixed(2)})`} accent />
              <ValueRow label="Trading days" value={reconciliation.final_accepted_headline.trading_days} />
              <ValueRow label="Methodology" value={reconciliation.final_accepted_headline.methodology} />
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Recorded {reconciliation.final_accepted_headline.recorded_at ? new Date(reconciliation.final_accepted_headline.recorded_at).toLocaleString('en-GB') : '—'} ·
              Source: {reconciliation.final_accepted_headline.source_evidence}
            </p>
          </SectionCard>
        )}

        {/* Native KPIs — Stage 8 Phase 7 (post 100:1 removal, 2026-02) */}
        {reconciliation?.native_kpis && (
          <SectionCard title="Corrected Native GP% & Yield% — Clone 356 (append-only, Session 336 untouched)" icon={BarChart3} className="border-indigo-200">
            <div className="mb-3 px-3 py-2 rounded text-sm font-semibold bg-indigo-50 border border-indigo-200 text-indigo-800"
              data-testid="native-kpis-status">
              100:1 quantity/base_quantity workaround removed · SALESELL no longer fed into AbbeyZone engine inputs · Session 336 remains locked (append-only evidence row).
            </div>
            <div data-testid="native-kpis-values" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <ValueRow label="Native GP%" value={`${Number(reconciliation.native_kpis.gp.native_gp_pct).toFixed(2)}%`} accent />
              <ValueRow label="GP% control" value={`${Number(reconciliation.native_kpis.gp.control_gp_pct).toFixed(2)}% (${reconciliation.native_kpis.gp.control_status})`} />
              <ValueRow label="Native Yield%" value={`${Number(reconciliation.native_kpis.yield.native_yield_pct).toFixed(2)}%`} accent />
              <ValueRow label="Yield legacy control" value={reconciliation.native_kpis.yield.legacy_control_status} />
              <ValueRow label="Accounts sales (exc VAT)" value={`£${Number(reconciliation.native_kpis.gp.accounts_sales_excl_vat).toFixed(2)}`} />
              <ValueRow label="Sales at cost" value={`£${Number(reconciliation.native_kpis.gp.sales_at_cost).toFixed(2)}`} />
              <ValueRow label="Cost of sales (physical)" value={`£${Number(reconciliation.native_kpis.gp.cost_of_sales_indirect).toFixed(2)} (legacy £${Number(reconciliation.native_kpis.gp.legacy_consumption_exc_vat).toFixed(2)})`} />
              <ValueRow label="Actual receipts (inc VAT)" value={`£${Number(reconciliation.native_kpis.yield.actual_receipts_incl_vat).toFixed(2)}`} />
              <ValueRow label="Theoretical after management" value={`£${Number(reconciliation.native_kpis.yield.theoretical_after_management_incl_vat).toFixed(2)}`} />
            </div>
            <div className="mt-3 space-y-1 text-xs text-slate-600" data-testid="native-kpis-formulae">
              <p><strong>GP% formula (native):</strong> {reconciliation.native_kpis.gp.native_formula}</p>
              <p><strong>GP% control formula:</strong> {reconciliation.native_kpis.gp.control_formula}</p>
              <p><strong>Yield% formula:</strong> {reconciliation.native_kpis.yield.native_formula}</p>
              <p className="text-slate-500 italic">{reconciliation.native_kpis.gp.methodology_note}</p>
              <p className="text-slate-500 italic">{reconciliation.native_kpis.yield.legacy_control_note}</p>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Recorded {reconciliation.native_kpis.recorded_at ? new Date(reconciliation.native_kpis.recorded_at).toLocaleString('en-GB') : '—'} ·
              Clone session: {reconciliation.native_kpis.clone_session_id} ·
              Session 336 integrity: {reconciliation.native_kpis.integrity.session_336_still_locked ? 'LOCKED ✓' : '⚠ UNLOCKED'}
            </p>
          </SectionCard>
        )}

        {/* Reconciliation Matrix */}
        {reconciliation && (
          <SectionCard title="Financial Reconciliation Matrix (original S7 accept run — superseded, retained for audit history)" icon={BarChart3}>
            <div className={`mb-3 px-3 py-2 rounded text-sm font-semibold ${reconciliation.all_deltas_zero ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}
              data-testid="recon-matrix-status">
              {reconciliation.all_deltas_zero
                ? `All ${reconciliation.matrix?.length} KPI deltas = 0.0000 at 4dp stored precision`
                : 'WARNING: Non-zero delta(s) detected — review matrix'}
            </div>
            {reconciliation.final_accepted_headline && (
              <p className="text-xs text-amber-600 mb-2" data-testid="recon-matrix-superseded-note">
                This matrix predates the Phase 0-5 non-circular correction. See "Final Accepted Headline" above for the figures that were actually mirrored onto locked session 336.
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse" data-testid="recon-matrix-table">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold">KPI</th>
                    <th className="text-right px-2 py-1.5 border border-slate-200 font-semibold">S7 Headline</th>
                    <th className="text-right px-2 py-1.5 border border-slate-200 font-semibold">Σ Product Lines</th>
                    <th className="text-right px-2 py-1.5 border border-slate-200 font-semibold">Delta</th>
                    <th className="text-center px-2 py-1.5 border border-slate-200 font-semibold">Pass</th>
                  </tr>
                </thead>
                <tbody>
                  {reconciliation.matrix?.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-2 py-1.5 border border-slate-200 font-medium text-slate-700">{row.kpi}</td>
                      <td className="px-2 py-1.5 border border-slate-200 text-right font-mono">
                        {row.s7_headline != null ? Number(row.s7_headline).toFixed(4) : '—'}
                      </td>
                      <td className="px-2 py-1.5 border border-slate-200 text-right font-mono">
                        {row.s7_product_lines_sum != null ? Number(row.s7_product_lines_sum).toFixed(4) : '—'}
                      </td>
                      <td className="px-2 py-1.5 border border-slate-200 text-right font-mono">
                        {row.delta_hl_vs_pl != null ? Number(row.delta_hl_vs_pl).toFixed(4) : '—'}
                      </td>
                      <td className="px-2 py-1.5 border border-slate-200 text-center">
                        {row.zero_delta === true ? '✓' : row.zero_delta === false ? '✗' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Locked session #{completedSession?.session_id || '310'} is the Route B snapshot of the above.
              Source and Excel pack match to the same precision — see test suite for column-sum assertions.
            </p>
          </SectionCard>
        )}
      </div>
    );

    return <p className="text-slate-400 text-sm">Step content not yet available.</p>;
  };

  const currentStep = STEPS[step];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking')} data-testid="back-button">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-base font-bold text-slate-900">Historical Stocktake Conversion</h1>
          <p className="text-xs text-slate-500">PSB Route B — {session?.session_reference || 'Loading…'}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {s7Plan?.status === 'accepted' && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Stage 7 Accepted</Badge>
          )}
          {s8Dispatch && (
            <Badge className="bg-blue-100 text-blue-800 border-blue-200">Stage 8 Dispatched</Badge>
          )}
        </div>
      </div>

      <div className="flex h-[calc(100vh-57px)]">
        {/* Sidebar */}
        <div className="w-64 shrink-0 bg-white border-r border-slate-200 overflow-y-auto">
          <div className="p-3 space-y-0.5">
            {STEPS.map((s, i) => {
              const status = stepStatuses[i] || 'pending';
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  data-testid={`wizard-step-${s.key}`}
                  onClick={() => setStep(i)}
                  className={`w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    i === step
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-slate-50 border border-transparent'
                  }`}
                >
                  <div className="mt-0.5 shrink-0">
                    {status === 'complete'
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      : status === 'active'
                      ? <Icon className="h-4 w-4 text-blue-600" />
                      : <Circle className="h-4 w-4 text-slate-300" />
                    }
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold truncate ${i === step ? 'text-blue-900' : status === 'complete' ? 'text-slate-700' : 'text-slate-400'}`}>
                      {i + 1}. {s.label}
                    </p>
                    <p className="text-[11px] text-slate-400 truncate mt-0.5">{s.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto space-y-1 mb-6">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{`Step ${step + 1} of ${STEPS.length}`}</Badge>
              <StepBadge status={stepStatuses[step] || 'pending'} />
            </div>
            <h2 className="text-xl font-bold text-slate-900">{currentStep.label}</h2>
            <p className="text-sm text-slate-500">{currentStep.desc}</p>
          </div>

          <div className="max-w-2xl mx-auto">
            {stepContent(currentStep.key)}
          </div>

          {/* Navigation */}
          <div className="max-w-2xl mx-auto mt-6 flex justify-between items-center">
            <Button variant="outline" size="sm" disabled={step === 0} onClick={() => setStep(s => s - 1)} data-testid="step-prev">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <span className="text-xs text-slate-400">{step + 1} / {STEPS.length}</span>
            <Button size="sm" disabled={step === STEPS.length - 1} onClick={() => setStep(s => s + 1)} data-testid="step-next">
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
